import { useQuery } from '@tanstack/react-query';
import { endOfDay, endOfHour, sub } from 'date-fns';
import { CurrencyDollarIcon } from '@phosphor-icons/react';
import { AreaChart } from './charts/area-chart';
import { ChangeBadge } from './change-badge';
import { Widget } from './widget';
import { useWidgetsContext } from './widgets-context';
import { getRevenueMetrics, getRevenueTimeseries } from './api/widgets-service';
import { numberWithCommas } from './lib/number-with-commas';
import type { DateRange, EventData } from './types';

const formatMoney = (value: number, currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: value >= 1000 ? 0 : 2,
  }).format(value);

export const RevenueWidget = ({ dateRange }: { dateRange: DateRange }) => {
  const { api, projectId } = useWidgetsContext();
  const prevDateRange = {
    interval: dateRange.interval,
    startDate: sub(dateRange.startDate, dateRange.period || { days: 1 }),
    endDate: dateRange.interval.includes('days')
      ? endOfDay(dateRange.startDate)
      : endOfHour(dateRange.startDate),
  };
  const range = { startDate: dateRange.startDate, endDate: dateRange.endDate };

  const { isLoading, data } = useQuery({
    queryKey: ['revenue-metrics', projectId, range],
    queryFn: () => getRevenueMetrics(api, { projectId, ...range }),
    retry: false,
  });
  const { data: prevData } = useQuery({
    queryKey: ['prev-revenue-metrics', projectId, prevDateRange],
    queryFn: () =>
      getRevenueMetrics(api, {
        projectId,
        startDate: prevDateRange.startDate,
        endDate: prevDateRange.endDate,
      }),
    retry: false,
  });
  const { data: series } = useQuery({
    queryKey: ['revenue-timeseries', projectId, range],
    queryFn: () => getRevenueTimeseries(api, { projectId, ...range }),
    retry: false,
  });

  const currency = data?.currency ?? 'USD';
  const total = data?.total_revenue ?? 0;
  const orders = data?.orders ?? 0;
  const prevTotal = prevData?.total_revenue ?? 0;

  // A project with no revenue in range and none before it has nothing to say
  // here, and every non-commerce project would otherwise carry an empty tile.
  if (!isLoading && orders === 0 && prevTotal === 0) {
    return null;
  }

  const chartData: EventData[] = (series?.series ?? []).map((point) => ({
    date: point.day,
    count: point.revenue,
  }));

  return (
    <Widget
      loading={isLoading}
      Header={() => (
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-xl font-medium">
            <CurrencyDollarIcon className="text-primary" />
            <span className="font-semibold">Revenue</span>
          </span>
          {!!data && (
            <div className="flex items-center gap-3">
              <span className="text-3xl leading-none font-black">
                {formatMoney(total, currency)}
              </span>
              <ChangeBadge current={total} previous={prevTotal} />
            </div>
          )}
        </div>
      )}
      SubHeader={() => (
        <div className="ml-5 flex items-center gap-4">
          <span className="text-xl font-bold">
            {numberWithCommas(orders)} Orders
          </span>
          <span className="text-muted-foreground text-xl font-bold">
            {formatMoney(data?.aov ?? 0, currency)} AOV
          </span>
        </div>
      )}
    >
      {!isLoading && (
        <AreaChart
          data={chartData}
          dateRange={dateRange}
          formatValue={(value) => formatMoney(value, currency)}
        />
      )}
    </Widget>
  );
};
